import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';

import {
  Inject,
  Component,
  OnDestroy,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';

import { SnAppliance, SnPullProtocol } from '@lib/api/entities';
import { SnAppliancesAPI } from '@lib/api/services';
import { SnApplianceFieldsGeneratorService } from '@lib/appliances';
import { SnFormFieldConfig, ISnHasForm } from '@lib/form';
import { SnLocationFieldsGeneratorService } from '@lib/locations';
import { t } from '@lib/translation';
import { SN_DRAWER_DATA, SnDrawerRef } from '@lib/ui/drawer';
@Component({
  selector: 'fs-appliance-form-drawer',
  templateUrl: './appliance-form-drawer.component.html',
  styleUrls: ['./appliance-form-drawer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ApplianceFormDrawerComponent implements ISnHasForm, OnDestroy {
  public appliance: SnAppliance;
  public readonly isNew: boolean;
  public readonly form = new UntypedFormGroup({});
  public formFields: SnFormFieldConfig[] = [];
  public formModel: any = {};
  public isLoading = false;
  private readonly _destroy$ = new Subject<void>();
  constructor(
    @Inject(SN_DRAWER_DATA) data: any,
    private readonly _appliancesAPI: SnAppliancesAPI,
    private readonly _drawerRef: SnDrawerRef<ApplianceFormDrawerComponent, SnAppliance>,
    private readonly _changeDetectorRef: ChangeDetectorRef,
    private readonly _applianceFieldsGeneratorService: SnApplianceFieldsGeneratorService,
    private readonly _locationFormFieldsGenerator: SnLocationFieldsGeneratorService,
  ) {
    this.isNew = !(data && data.id);
    this.appliance = data;
    if (!this.isNew) {
      this.isLoading = true;
      this._appliancesAPI
        .getAppliance(data.id)
        .pipe(takeUntil(this._destroy$))
        .subscribe({
          next: (appliance) => {
            this.appliance = appliance;
            this._setForm();
            this._patchForm(this.appliance);
            this.isLoading = false;
            this._changeDetectorRef.detectChanges();
          },
          error: () => {
            this.isLoading = false;
            this._changeDetectorRef.detectChanges();
          },
        });
    }
  }
  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }
  public cancel(): void {
    this._drawerRef.close();
  }
  public submitForm(): void {
    const data = this.formModel;
    this.isLoading = true;
    this._updateAppliance(data);
  }
  private _patchForm(appliance: SnAppliance): void {
    if (appliance?.location?.id) {
      this.formModel = {
        ...appliance,
        location: appliance.location.id,
      };
    } else {
      this.formModel = {
        ...appliance,
      };
    }
  }
  private _updateAppliance(formData: Object): void {
    this._appliancesAPI
      .updateAppliance(this.appliance.id, formData)
      .pipe(
        takeUntil(this._destroy$),
        finalize(() => {
          this.isLoading = false;
          this._changeDetectorRef.markForCheck();
        }),
      )
      .subscribe((appliance) => {
        this._drawerRef.close(appliance);
      });
  }
  private _setForm(): void {
    this.formFields = [
      this._applianceFieldsGeneratorService.getNameField({
        templateOptions: {
          required: true,
        },
      }),
      {
        templateOptions: {
          label: t('appliances.labels.device'),
          displayGroupStatus: true,
        },
        fieldGroup: [
          this._applianceFieldsGeneratorService.getMakeField({
            templateOptions: {
              disabled: true,
            },
          }),
          this._applianceFieldsGeneratorService.getModelField({
            templateOptions: {
              disabled: true,
            },
          }),
          this._applianceFieldsGeneratorService.getSerialNumberField({
            templateOptions: {
              required: true,
              disabled: true,
            },
          }),
        ],
        wrappers: ['form-field-group'],
      },
      {
        templateOptions: {
          label: t('common.location'),
          displayGroupStatus: true,
        },
        fieldGroup: [
          this._locationFormFieldsGenerator.getLocationSelectField(
            {
              templateOptions: {
                skipLoadMissing: true,
              },
            },
            {
              id__in: this.appliance?.location?.id,
            },
          ),
        ],
        wrappers: ['form-field-group'],
      },
      {
        templateOptions: {
          label: t('appliances.labels.publishSettings'),
          displayGroupStatus: true,
        },
        fieldGroup: [
          this._applianceFieldsGeneratorService.getServerIdField({
            templateOptions: {
              required: true,
              disabled: true,
            },
          }),
          this._applianceFieldsGeneratorService.getHostField({
            templateOptions: {
              required: true,
              disabled: true,
            },
          }),
          {
            fieldGroupClassName: 'form-field-row',
            fieldGroup: [
              this._applianceFieldsGeneratorService.getRtmpPortField({
                className: 'form-field-column',
                templateOptions: {
                  required: true,
                  disabled: true,
                },
              }),
              this._applianceFieldsGeneratorService.getRtspPortField({
                className: 'form-field-column',
                templateOptions: {
                  required: true,
                  disabled: true,
                },
              }),
              this._applianceFieldsGeneratorService.getHttpPortField({
                className: 'form-field-column',
                templateOptions: {
                  disabled: true,
                },
              }),
            ],
          },
        ],
        wrappers: ['form-field-group'],
      },
      {
        templateOptions: {
          label: t('appliances.labels.pullSettings'),
        },
        fieldGroup: [
          this._applianceFieldsGeneratorService.getPullProtocolField({
            defaultValue: SnPullProtocol.RTSP,
            templateOptions: {
              required: true,
              disabled: true,
              change: (field) => {
                const value = field.formControl.value;
                this.form.get('pullSsl').setValue(value !== SnPullProtocol.RTSP);
              },
            },
          }),
          this._applianceFieldsGeneratorService.getPullSslField({
            defaultValue: false,
            templateOptions: {
              disabled: true,
            },
          }),
        ],
        wrappers: ['form-field-group'],
      },
    ];
  }
}
